import { fobiCore } from '../../FobiCore';

/**
 * Erkennt in einem gescannten oder eingetippten Text die EFN und alles, was sich
 * darueber hinaus als Beschriftung verwenden laesst.
 *
 * Hintergrund: die Teilnehmer-App der LAEKH (fobiTeilnehmer) legt wahlweise die nackte
 * EFN oder "<EFN> <Anrede>" in den QR-Code. Andere Apps packen die EFN mit weiteren
 * Feldern zusammen, etwa "802760600165921,Oleg,Mustermann,oleg@gurevich.de", und das
 * Trennzeichen ist von App zu App verschieden. Wieder andere schreiben JSON.
 *
 * Bewusst eng definiert, was hier als "Muell" gilt: Trennzeichen, lange reine
 * Ziffernbloecke (Mitglieds-, Kunden- oder Veranstaltungsnummern) und Interpunktionsreste.
 * Alles darueber hinaus waere Raten - die Reihenfolge wird deshalb auch nicht umsortiert,
 * denn "Nachname,Vorname" und "Vorname Nachname" sind von aussen nicht zu unterscheiden
 * (dieselbe Ueberlegung wie bei removeFremdinhalt in FobiCore). Der Anwender sieht die
 * Beschriftung vor der Uebernahme und kann sie mit einem Klick korrigieren.
 */

const EMAIL_REGEX = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const EFN_SCHLUESSEL = /^efn$/i;
const EMAIL_SCHLUESSEL = /^(e[-_]?mail|mail)$/i;
/** Reihenfolge ist die Ausgabereihenfolge, nicht nur eine Erkennungsliste. */
const NAMENS_SCHLUESSEL = ['anrede', 'titel', 'vorname', 'nachname', 'name', 'teilnehmer', 'label', 'beschriftung'];
/**
 * "Nur Interpunktion" heisst: kein einziger Buchstabe und keine Ziffer - in *keiner* Schrift.
 * Mit einer Latin-1-Klasse fiel hier ein kyrillischer oder griechischer Name komplett heraus:
 * jedes seiner Woerter galt als Interpunktionsrest, uebrig blieb eine leere Beschriftung.
 */
const NUR_INTERPUNKTION = /^[^\p{L}\p{N}]+$/u;
const LANGE_ZIFFERNFOLGE = /^\d{6,}$/;
const MAX_LAENGE_BESCHRIFTUNG = 60;

const LEERES_ERGEBNIS = { efn: undefined, text: '', email: '' };

/**
 * Ein Name in Anfuehrungszeichen ist ein Name, keine Zeichenkette mit Anfuehrungszeichen:
 * "Leonie Braun", 802... soll "Leonie Braun" heissen.
 *
 * Abgestreift wird **nur ein umschliessendes Paar**. Blindes Trimmen an beiden Enden machte aus
 * `Praxis "Am Markt"` ein unpaariges `Praxis "Am Markt` - schlimmer als der Anfangszustand.
 */
const ANFUEHRUNGSPAARE = [['"', '"'], ['“', '”'], ['„', '“'], ['„', '”'], ['‟', '”'], ['«', '»'], ['»', '«']];

const ohneUmschliessendeAnfuehrungszeichen = (text) => {
	const paar = ANFUEHRUNGSPAARE.find(([auf, zu]) =>
		text.length >= auf.length + zu.length + 1 && text.startsWith(auf) && text.endsWith(zu));
	return paar ? text.slice(paar[0].length, -paar[1].length).trim() : text;
};

/**
 * Raeumt einen Textrest zu einer anzeigbaren Beschriftung auf. Auch einzeln verwendbar,
 * wenn der Anwender selbst etwas eingegeben hat.
 */
export const bereinigeBeschriftung = (text) => {
	const ohneTrennzeichen = (text || '').replace(/[,;|/\\\t\r\n]+/g, ' ');
	const tokens = ohneTrennzeichen.split(/\s+/)
		.filter(token => token)
		.filter(token => !LANGE_ZIFFERNFOLGE.test(token))
		.filter(token => !NUR_INTERPUNKTION.test(token));
	const zusammengesetzt = ohneUmschliessendeAnfuehrungszeichen(tokens.join(' ')
		.replace(/^[-–—.,:;|\s]+/, '')
		.replace(/[-–—,:;|\s]+$/, ''));
	return zusammengesetzt.length > MAX_LAENGE_BESCHRIFTUNG
		? zusammengesetzt.substring(0, MAX_LAENGE_BESCHRIFTUNG).trim()
		: zusammengesetzt;
};

/**
 * Schneidet das erste freistehende Vorkommen der EFN heraus - freistehend heisst: keine
 * Ziffer davor oder dahinter, damit nicht mitten aus einer laengeren Nummer geschnitten wird.
 */
const entferneEFN = (text, efn) => {
	let index = text.indexOf(efn);
	while (index !== -1) {
		const zeichenDavor = index > 0 ? text.charAt(index - 1) : '';
		const zeichenDanach = text.charAt(index + efn.length);
		if (!/\d/.test(zeichenDavor) && !/\d/.test(zeichenDanach)) {
			return text.substring(0, index) + ' ' + text.substring(index + efn.length);
		}
		index = text.indexOf(efn, index + 1);
	}
	return text;
};

/** Flacht ein geparstes JSON zu Schluessel/Wert-Paaren ab; Tiefe begrenzt, damit nichts entgleist. */
const sammleEintraege = (wert, tiefe, eintraege) => {
	if (tiefe > 3 || wert === null || wert === undefined) return eintraege;
	if (Array.isArray(wert)) {
		wert.forEach(element => sammleEintraege(element, tiefe + 1, eintraege));
		return eintraege;
	}
	if (typeof wert === 'object') {
		Object.keys(wert).forEach(schluessel => {
			const unterwert = wert[schluessel];
			if (unterwert !== null && typeof unterwert === 'object') {
				sammleEintraege(unterwert, tiefe + 1, eintraege);
			} else if (unterwert !== null && unterwert !== undefined) {
				eintraege.push({ schluessel: String(schluessel), wert: String(unterwert).trim() });
			}
		});
		return eintraege;
	}
	eintraege.push({ schluessel: '', wert: String(wert).trim() });
	return eintraege;
};

const findeEmail = (eintraege) => {
	const benannt = eintraege.find(e => EMAIL_SCHLUESSEL.test(e.schluessel) && EMAIL_REGEX.test(e.wert));
	const treffer = (benannt || eintraege.find(e => EMAIL_REGEX.test(e.wert)) || { wert: '' }).wert.match(EMAIL_REGEX);
	return treffer ? treffer[0] : '';
};

const findeBeschriftung = (eintraege, efn, email) => {
	const benannte = NAMENS_SCHLUESSEL
		.map(name => eintraege.filter(e => e.schluessel.toLowerCase() === name))
		.reduce((alle, treffer) => alle.concat(treffer), [])
		.map(e => e.wert);
	if (benannte.length > 0) return bereinigeBeschriftung(benannte.join(' '));
	// Kein bekannter Schluessel dabei: alles nehmen, was nicht EFN oder E-Mail ist.
	const uebrige = eintraege
		.filter(e => e.wert !== efn && e.wert !== email && !EMAIL_REGEX.test(e.wert))
		.map(e => e.wert);
	return bereinigeBeschriftung(uebrige.join(' '));
};

/** Traegt die gesamte Eingabe ein JSON-Objekt? Dann sind Schluessel benannt und muessen nicht geraten werden. */
const istJson = (rohtext) => {
	const text = (rohtext || '').trim();
	if(!(text.startsWith('{') || text.startsWith('['))) return false;
	try {
		return JSON.parse(text) !== null && typeof JSON.parse(text) === 'object';
	} catch (e) {
		return false;
	}
};

/** Gibt null zurueck, wenn es kein verwertbares JSON ist - der Aufrufer geht dann den Textweg. */
const ausJson = (rohtext) => {
	let geparst;
	try {
		geparst = JSON.parse(rohtext);
	} catch (e) {
		return null;
	}
	if (geparst === null || typeof geparst !== 'object') return null;
	const eintraege = sammleEintraege(geparst, 0, []);
	if (eintraege.length === 0) return null;
	const benannteEFN = eintraege.find(e => EFN_SCHLUESSEL.test(e.schluessel) && fobiCore.isEFN(e.wert));
	const irgendeineEFN = eintraege.find(e => fobiCore.isEFN(e.wert));
	const efn = (benannteEFN || irgendeineEFN || {}).wert;
	const email = findeEmail(eintraege);
	return { efn, text: findeBeschriftung(eintraege, efn, email), email };
};

/**
 * `email` ist ein **Filterwert, kein Feld**: die Adresse wird erkannt, um sie aus dem Namen
 * herauszuschneiden und um sie in einer Tabellenzeile nicht als Namensspalte zu verwechseln
 * (`alsTabellenzeile`, `zaehleWeitereSpalten`). Gespeichert, angezeigt, gedruckt oder exportiert
 * wird sie seit September 2026 nirgends mehr - wer sie von hier aus wieder weiterreicht, holt
 * genau das zurueck, was bewusst entfernt wurde.
 *
 * @param {string} rohtext Barcode-Inhalt oder Feldeingabe
 * @returns {{efn: (string|undefined), text: string, email: string}} wirft nie
 */
export const parseEFNEingabe = (rohtext) => {
	const text = typeof rohtext === 'string' ? rohtext.trim() : '';
	if (!text) return { ...LEERES_ERGEBNIS };
	if (text.startsWith('{') || text.startsWith('[')) {
		const ausJsonErkannt = ausJson(text);
		if (ausJsonErkannt) return ausJsonErkannt;
	}
	const efn = fobiCore.findEFNInText(text);
	let rest = efn ? entferneEFN(text, efn) : text;
	const emailTreffer = rest.match(EMAIL_REGEX);
	const email = emailTreffer ? emailTreffer[0] : '';
	if (email) rest = rest.replace(email, ' ');
	return { efn, text: bereinigeBeschriftung(rest), email };
};

/**
 * Fuer das Eingabefeld: liefert nur dann ein Ergebnis, wenn die Zuordnung Text -> EFN
 * eindeutig ist.
 *
 * Zwei Bedingungen, beide notwendig. Genau eine gueltige EFN - bei mehreren ist nicht zu
 * entscheiden, zu welcher der Name gehoert. Und keine verdaechtige Nummer im Feld: eine
 * vertippte EFN wuerde sonst stillschweigend im Beschriftungstext verschwinden, statt wie
 * bisher als "ungueltige entfernen" zur Entscheidung gestellt zu werden.
 */
export const parseEinzelneEFNMitBeschriftung = (eingabe) => {
	const text = typeof eingabe === 'string' ? eingabe : '';
	if(fobiCore.extractEFNsToArray(text).length !== 1) return null;
	if(fobiCore.extractVerdaechtigeEFNsToArray(text).length > 0) return null;
	const geparst = parseEFNEingabe(text);
	if(!geparst.efn) return null;
	if(!hatBeifang(geparst)) return null;
	// Die Wortgrenze gilt fuer *Text*, nicht fuer JSON: dort zaehlt sie Klammern, Schluessel und
	// Anfuehrungszeichen mit und verwirft jedes strukturierte Objekt - der Name ginge verloren,
	// obwohl er sauber benannt danebensteht.
	if(!istJson(text) && !istPlausibleBeschriftungslaenge(text)) return null;
	return geparst;
};

/** Mehr Woerter als das traegt kein Name und keine Notiz. */
const MAX_WORTE_BESCHRIFTUNG = 8;

/**
 * Wer eine ganze Tabellenzeile einfuegt, will keine Beschriftung: aus "EFN Name / Notiz
 * Teilnahme (5 Pkt.) Referent, Moderation, wiss. Leitung Meldedatum" wuerde sonst der Name der
 * EFN. Seit die Eingabe zeilenweise gelesen wird (listenEingabe.js), faellt die Kopfzeile als
 * eigene Zeile heraus - die Grenze bleibt trotzdem noetig, denn eine *breite* Tabellenzeile ist
 * ebenfalls kein Name: ein Name mit Titeln kommt auf fuenf, sechs Woerter, eine Tabellenzeile
 * auf ein Vielfaches.
 *
 * Ueber der Grenze greift der gewohnte Weg ueber "bereinigen" - kein Datenverlust, nur eine
 * bewusste Handbewegung mehr. Die Fehlerrichtung ist damit die richtige: im Zweifel keine
 * Beschriftung, statt Beifang als Namen an einer EFN.
 *
 * Bewusst nur hier und nicht in parseEFNEingabe: ein Barcode traegt, was er traegt, und
 * niemand fuegt dort eine Tabelle ein.
 */
const istPlausibleBeschriftungslaenge = (eingabe) => {
	const worte = eingabe.split(/[\s,;|/\\]+/).filter(wort => wort);
	return worte.length - 1 <= MAX_WORTE_BESCHRIFTUNG;
};

const zerlegeZumVergleich = (text) => (text || '')
	.toLowerCase()
	// Umlaute deutsch ausschreiben, sonst gehen "Mueller" und "Müller" auseinander.
	.replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
	.replace(/[àáâ]/g, 'a').replace(/[òóô]/g, 'o').replace(/[ùúû]/g, 'u').replace(/[éèê]/g, 'e')
	// An allem trennen, was kein Buchstabe und keine Ziffer ist - \p{L} statt a-z. Mit der
	// ASCII-Klasse zerfiel ein kyrillischer oder griechischer Name in *nichts*, und eine leere
	// Wortfolge widerspricht nichts: "Иван Иванов" und "Петр Петров" galten als dieselbe Person
	// und wurden stillschweigend zusammengefuehrt - genau die Verwechslung, gegen die
	// istGleicherNameStreng gebaut ist.
	.split(/[^\p{L}\p{N}]+/u)
	.filter(wort => wort);

/**
 * Meinen zwei Beschriftungen dieselbe Person?
 *
 * Blosses Normalisieren reicht nicht: "O. Mustermann" und "Oleg Mustermann" waeren dann ein
 * Widerspruch, obwohl es dieselbe Person ist. Regel deshalb: die kuerzere Wortfolge muss sich
 * der Reihe nach in der laengeren wiederfinden, wobei ein Wort als Abkuerzung des anderen
 * zaehlt. Damit fallen weggelassene Titel ("Max Mustermann" vs. "Dr. med. Max Mustermann")
 * und abgekuerzte Vornamen durch, ein anderer Nachname nicht.
 *
 * Im Zweifel gleich: eine leere Seite widerspricht nichts.
 */
export const istGleicheBeschriftung = (einText, andererText) => {
	const einer = zerlegeZumVergleich(einText);
	const anderer = zerlegeZumVergleich(andererText);
	if(einer.length === 0 || anderer.length === 0) return true;
	const [kurz, lang] = einer.length <= anderer.length ? [einer, anderer] : [anderer, einer];
	let gefunden = 0;
	lang.forEach(wort => {
		if(gefunden >= kurz.length) return;
		if(wort.startsWith(kurz[gefunden]) || kurz[gefunden].startsWith(wort)) gefunden++;
	});
	return gefunden === kurz.length;
};

/**
 * Titel und Anreden, die fuer die Frage "derselbe Name?" nicht mitzaehlen. Bewusst hier und
 * nicht im Druck-Modul: das ist eine Aussage ueber Namen, keine ueber Papier.
 */
const TITEL = ['herr', 'frau', 'dr', 'prof', 'dipl', 'med', 'dent', 'vet', 'rer', 'nat', 'univ', 'habil', 'mag', 'ing'];

const ohneTitel = (worte) => worte.filter(wort => TITEL.indexOf(wort) < 0);

/**
 * Dieselbe Frage wie istGleicheBeschriftung, aber streng - und der Unterschied ist Absicht.
 *
 * istGleicheBeschriftung laesst *jedes* Wortpraefix als Abkuerzung durchgehen. Fuer die Frage
 * "widerspricht dieser Scan dem, was dasteht?" ist das richtig: es haelt die Warnung frei von
 * Rauschen, und beide Namen bleiben ohnehin erhalten. Fuer die Frage "darf ich diese beiden
 * Zeilen zu einer Beschriftung zusammenfassen?" waere es gefaehrlich - "Max Mustermann" und
 * "Maximilian Mustermann" wuerden zu einer Person, "Anna" und "Annabelle" ebenso, und die
 * Punktemeldung landete moeglicherweise am falschen Menschen.
 *
 * Streng heisst: gleiche Anzahl Namensteile (Titel zaehlen nicht mit) und jeder Teil entweder
 * identisch oder eine echte Ein-Buchstaben-Initiale. Ein falsch erkannter Widerspruch kostet
 * einen Klick - eine falsche Zusammenfuehrung kostet mehr.
 */
export const istGleicherNameStreng = (einText, andererText) => {
	const einer = ohneTitel(zerlegeZumVergleich(einText));
	const anderer = ohneTitel(zerlegeZumVergleich(andererText));
	// Im Zweifel gleich: eine leere Seite widerspricht nichts (wie bei istGleicheBeschriftung).
	if(einer.length === 0 || anderer.length === 0) return true;
	if(einer.length !== anderer.length) return false;
	return einer.every((wort, stelle) => {
		const gegen = anderer[stelle];
		if(wort === gegen) return true;
		if(wort.length === 1 && gegen.startsWith(wort)) return true;
		if(gegen.length === 1 && wort.startsWith(gegen)) return true;
		return false;
	});
};

/**
 * Traegt die Zeile neben der EFN ueberhaupt etwas? **Eine Frage der Gueltigkeit, nicht des
 * Namens** - `parseEinzelneEFNMitBeschriftung` haengt daran, und deren `null` fuehrt in
 * `ermittleStatus` zu 'ungueltig'. Bei "802760600165921 max@example.de" scheitert
 * `validateMultipleEFNs`, es bleibt also allein an diesem Zweig: auf `text` verkuerzt, wuerde die
 * Teilnahme nicht mehr uebernommen. EFN plus Adresse ohne Namen ist eine gueltige Teilnahme -
 * gespeichert wird die Adresse trotzdem nicht.
 *
 * Hiess bis September 2026 `hatBeschriftung`. Der Name war unwahr, seit eine Beschriftung nur noch
 * aus Text besteht, und hat prompt zu dem Fehlschluss gefuehrt, man koenne ihn auf `text`
 * verkuerzen.
 */
export const hatBeifang = (beschriftung) =>
	Boolean(beschriftung && ((beschriftung.text || '').trim() || (beschriftung.email || '').trim()));

/**
 * Traegt sie einen **Namen**? Das ist die andere Frage - sie entscheidet, ob etwas gespeichert
 * und angezeigt wird (Reducer, Scanner-Meldung). Eine reine E-Mail-Adresse ist Beifang, aber kein
 * Namenstext.
 */
export const hatNamenstext = (beschriftung) =>
	Boolean(beschriftung && (beschriftung.text || '').trim());

export const efnBarcodeContentRecogniser = { parseEFNEingabe, parseEinzelneEFNMitBeschriftung,
	bereinigeBeschriftung, hatBeifang, hatNamenstext, istGleicheBeschriftung, istGleicherNameStreng };
